import * as Sentry from '@sentry/browser';
import { useCallback, useMemo, useState } from 'react';
import { getPlaceDetails, getPlacePredictions } from './places';

export const useGooglePlaces = (options?: {
    blacklistedCountries?: Set<string>;
}) => {
    const { blacklistedCountries } = options || {};
    const blacklistedSet = useMemo(
        () => blacklistedCountries ?? new Set<string>(),
        [blacklistedCountries],
    );
    const [places, setPlaces] = useState<
        google.maps.places.AutocompletePrediction[]
    >([]);

    const search = useCallback(
        async (query: string) => {
            if (!query) {
                setPlaces([]);
                return;
            }

            try {
                const data = await getPlacePredictions(query, {
                    blacklistedCountries: blacklistedSet,
                });
                setPlaces(data ?? []);
            } catch (e) {
                Sentry.captureException(e, {
                    tags: { service: 'googleMaps' },
                    extra: { message: 'Google maps get places error' },
                });
                setPlaces([]);
            }
        },
        [blacklistedSet],
    );

    const getDetails = useCallback(async (placeId: string) => {
        try {
            const data = await getPlaceDetails(placeId);
            return data;
        } catch (e) {
            Sentry.captureException(e, {
                tags: { service: 'googleMaps' },
                extra: { message: 'Google maps get place details error' },
            });
            return null;
        }
    }, []);

    return {
        places,
        search,
        getDetails,
    };
};
